import React, {
  Fragment,
  PropsWithChildren,
  useCallback,
  useState,
} from "react";

import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useDeleteHousehold from "@fartherfinance/frontend/api/Households/hooks/useDeleteHousehold";
import usePutHousehold from "@fartherfinance/frontend/api/Households/hooks/usePutHousehold";
import { HouseholdId } from "@fartherfinance/frontend/api/Households/types";

import { HouseholdProvider, useHouseholdContext } from "../context";
import HouseholdActionMenu, {
  HouseholdMenuActions,
} from "../HouseholdsTable/HouseholdActionMenu";
import { HouseholdFormWithMembers } from "../types";
import {
  formatEditHouseholdResponse,
  formatHouseholdDefaultFormFields,
} from "../utils";
import Drawer, {
  extraLargeDrawer,
} from "@src/multiCustodian/components/Drawer/Drawer";
import { useAdvisorPageInit } from "@src/multiCustodian/hooks/Advisor/useAdvisorPageInit";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Households from "@src/multiCustodian/pages/Advisor/Households/Households";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import DrawerHero from "@src/sharedComponents/DrawerHero/DrawerHero";
import DeleteModal from "@src/sharedComponents/Modal/DeleteModal/DeleteModal";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import { handleApiError } from "@src/utils/handleApiError";

import HouseholdsDrawerTabs, { HouseholdTabPath } from "./HouseholdsDrawerTabs";

interface Props {
  tab?: HouseholdTabPath;
  footer?: React.ReactNode;
}

const HouseholdsDrawerContent = (props: PropsWithChildren<Props>) => {
  const { householdId, household, form, isLoading } = useHouseholdContext();

  const { auth } = useAdvisorPageInit();

  const navigate = useNavigate();

  const location = useLocation();

  const notify = useStatusNotification();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { mutateAsync: deleteHousehold } = useDeleteHousehold(auth!);

  const closeDrawer = useCallback((): void => {
    navigate({ pathname: "/Advisor/Clients", search: location.search });
  }, [navigate, location.search]);

  const householdDisplayName = household?.name || "";

  const handleCancel = useCallback(() => {
    form.reset(formatHouseholdDefaultFormFields(household));
    closeDrawer();
  }, [form, household, closeDrawer]);

  const handleDeleteHousehold = useCallback(async () => {
    if (!householdId) {
      return;
    }

    try {
      await deleteHousehold({ householdId });

      // Track household deletion
      trackEvent({
        name: "Advisor Delete Household",
        properties: {
          householdId,
          householdName: household?.name,
        },
      });

      setShowDeleteModal(false);

      notify("Household deleted successfully", "Success");

      handleCancel();
    } catch (error) {
      handleApiError({
        error: error,
        errorMessage: "Error deleting household:",
        genericErrorMessage: "Error deleting household",
        notify,
      });
    }
  }, [householdId, deleteHousehold, notify, handleCancel, household]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleActionSelected = useCallback((action: HouseholdMenuActions) => {
    switch (action) {
      case HouseholdMenuActions.Delete:
        setShowDeleteModal(true);
        break;
      default:
        break;
    }
  }, []);

  return (
    <Drawer
      isDrawerOpen
      onClose={closeDrawer}
      footer={props.footer}
      container_style={extraLargeDrawer}
    >
      <DrawerHero
        start={
          <Fragment>
            {isLoading ? (
              <Skeleton width={300} height={44} />
            ) : (
              <>
                <TitleBlock title={householdDisplayName} />
                {household && (
                  <HouseholdActionMenu
                    household={household}
                    onActionSelected={handleActionSelected}
                  />
                )}
              </>
            )}
          </Fragment>
        }
      />
      <HouseholdsDrawerTabs>{props.children}</HouseholdsDrawerTabs>
      <DeleteModal
        isOpen={showDeleteModal}
        title="Delete Household"
        text={`Deleting "${
          household?.name ?? ""
        }" will remove it and all associated members. This action cannot be undone.`}
        deleteButtonText="Delete Household"
        onDelete={handleDeleteHousehold}
        onClose={handleCloseDeleteModal}
      />
    </Drawer>
  );
};

const HouseholdsDrawer = (props: PropsWithChildren<Props>) => {
  const { householdId, tab } = useParams<{
    householdId: HouseholdId;
    tab: HouseholdTabPath;
  }>();

  const navigate = useNavigate();
  const location = useLocation();
  const notify = useStatusNotification();
  const auth = useAdvisorRequestAuth();

  const { mutateAsync: updateHousehold } = usePutHousehold(auth);

  const closeDrawer = useCallback((): void => {
    navigate({ pathname: "/Advisor/Clients", search: location.search });
  }, [navigate, location.search]);

  const handleOnSaveHousehold = async (formData: HouseholdFormWithMembers) => {
    if (!householdId) {
      return;
    }

    try {
      await updateHousehold({
        householdId,
        body: formatEditHouseholdResponse(formData),
      });

      // Track household update
      trackEvent({
        name: "Advisor Update Household",
        properties: {
          householdId,
          householdName: formData.householdName,
        },
      });

      notify("Household updated successfully", "Success");
    } catch (error) {
      handleApiError({
        error: error,
        errorMessage: "Error updating household",
        genericErrorMessage: "Error updating household",
        notify,
      });
    }
  };

  const handleCancel = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <Households>
      <HouseholdProvider
        householdId={householdId}
        onSave={handleOnSaveHousehold}
        onCancel={handleCancel}
      >
        <HouseholdsDrawerContent {...props} tab={tab} />
      </HouseholdProvider>
    </Households>
  );
};

export default HouseholdsDrawer;
